import { ChangeEvent, useCallback, useMemo } from 'react';
import { FormField } from '@nimbus-ds/patterns';
import { DeliveryZonesScope } from '@tiendanube/common/src/enums';
import { InputNumberNimbus, Stack } from 'commons/components';
import { useModal } from 'commons/hooks';
import { useGetProvinces } from 'domains/Shipping/Addresses/hooks';
import { SelectedZonesInterface } from 'domains/Shipping/components/shared/ZoneManagement/DeliveryZoneCard/DeliveryZoneCard';
import SelectProvinceModal from 'domains/Shipping/components/shared/ZoneManagement/SelectModals/SelectProvinceModal';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import { useUpsellProtectedActions } from '../../../../../hooks';
import { createSyntheticChangeEvent } from '../../../../ShippingRestrictionPages/utils';
import OptionsSection from '../../../OptionsSection';
import SelectedItemsChipList from '../../../SelectedItemsChipList';
import { RadioOption } from '../../../shared/types';

interface DeliveryZonesSectionProps {
  deliveryZones?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  selectedZones: SelectedZonesInterface[];
  distanceInKm: string;
  onDeliveryZonesChange: (zones: SelectedZonesInterface[]) => void;
  errors?: Partial<{ distanceInKm: string }>;
}

function DeliveryZonesSection({
  deliveryZones = DeliveryZonesScope.ALL,
  onChange,
  selectedZones,
  distanceInKm,
  onDeliveryZonesChange,
  errors = {},
}: Readonly<DeliveryZonesSectionProps>): JSX.Element {
  const [showModal, openModal, closeModal] = useModal();
  const { provinces } = useGetProvinces();

  const t = useTranslationShipping(
    'deliveryMethods.shippingLimiting.availability',
  );

  const resolvedZones = useMemo(
    (): SelectedZonesInterface[] =>
      selectedZones.map((zone) => {
        const province = provinces?.find((p) => p.code === zone.provinceCode);
        return {
          ...zone,
          provinceId: province?.id || zone.provinceId || '',
          provinceName: province?.name || zone.provinceName || '',
        };
      }),
    [selectedZones, provinces],
  );

  const { withUpsellCheck } = useUpsellProtectedActions({
    onUpsellDecline: () => {
      onChange(
        createSyntheticChangeEvent(
          'deliveryZonesScope',
          DeliveryZonesScope.ALL,
        ),
      );
      onDeliveryZonesChange([]);
    },
  });

  const options: RadioOption[] = [
    {
      label: t('deliveryZones.all'),
      value: DeliveryZonesScope.ALL,
    },
    {
      label: t('deliveryZones.stateOrCity'),
      value: DeliveryZonesScope.GEOGRAPHICAL_ZONE,
    },
    {
      label: t('deliveryZones.distanceInKm'),
      value: DeliveryZonesScope.DISTANCE_IN_KM,
    },
  ];

  const handleChange = withUpsellCheck(
    useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        onChange(e);

        if (
          e.target.value === DeliveryZonesScope.GEOGRAPHICAL_ZONE &&
          selectedZones.length === 0
        ) {
          openModal();
        }
      },
      [onChange, selectedZones.length, openModal],
    ),
  );

  const handleChangeZones = useCallback(
    (zones: SelectedZonesInterface[]) => {
      onDeliveryZonesChange(zones);
      closeModal();
    },
    [onDeliveryZonesChange, closeModal],
  );

  const handleCloseProvinceModal = useCallback(() => {
    if (selectedZones.length === 0) {
      onChange(
        createSyntheticChangeEvent(
          'deliveryZonesScope',
          DeliveryZonesScope.ALL,
        ),
      );
    }
    closeModal();
  }, [selectedZones.length, onChange, closeModal]);

  const handleRemoveZone = withUpsellCheck(
    useCallback(
      (item: string | number) => {
        const itemString = item.toString();
        const updatedZones = selectedZones.filter(
          (zone) => zone.provinceCode !== itemString,
        );

        if (updatedZones.length === 0) {
          onChange(
            createSyntheticChangeEvent(
              'deliveryZonesScope',
              DeliveryZonesScope.ALL,
            ),
          );
        }

        onDeliveryZonesChange(updatedZones);
      },
      [onDeliveryZonesChange, selectedZones, onChange],
    ),
  );

  const getZoneName = useCallback(
    (id: string | number): string => {
      const zone = resolvedZones.find((z) => z.provinceCode === id.toString());
      if (!zone) return id.toString();

      const citiesCount = zone.cities.length;

      if (citiesCount === 0) {
        const allCitiesLabel = t('deliveryZones.allCities');
        return `${zone.provinceName} (${allCitiesLabel})`;
      }

      return `${zone.provinceName} (${citiesCount})`;
    },
    [resolvedZones, t],
  );

  return (
    <>
      <Stack column align="stretch">
        <Stack.Item>
          <OptionsSection
            title={t('deliveryZones.title')}
            name="deliveryZonesScope"
            options={options}
            value={deliveryZones}
            onChange={handleChange}
          />
        </Stack.Item>
        {deliveryZones === DeliveryZonesScope.GEOGRAPHICAL_ZONE &&
          resolvedZones.length > 0 && (
            <Stack.Item>
              <SelectedItemsChipList
                items={resolvedZones.map((zone) => zone.provinceCode)}
                editLinkText={t('deliveryZones.editLink')}
                getItemText={getZoneName}
                onEdit={withUpsellCheck(openModal)}
                onRemove={handleRemoveZone}
              />
            </Stack.Item>
          )}
        {deliveryZones === DeliveryZonesScope.DISTANCE_IN_KM && (
          <Stack.Item>
            <FormField
              label={t('deliveryZones.distanceLabel')}
              showHelpText
              helpText={
                errors.distanceInKm ? t(errors.distanceInKm) : undefined
              }
            >
              <InputNumberNimbus
                type="integer"
                name="deliveryDistanceInKm"
                value={distanceInKm || ''}
                onChange={onChange}
                append={t('deliveryZones.distanceUnit')}
                appendPosition="end"
                placeholder="0"
                appearance={errors.distanceInKm ? 'danger' : 'neutral'}
              />
            </FormField>
          </Stack.Item>
        )}
      </Stack>

      {showModal && (
        <SelectProvinceModal
          isOpen
          onClose={handleCloseProvinceModal}
          onChange={handleChangeZones}
          selectedZones={resolvedZones}
        />
      )}
    </>
  );
}

export default DeliveryZonesSection;
